import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { closeCart } from "../../redux/slices/uiSlice";
import { Link, useNavigate } from "react-router-dom";
import { X } from "lucide-react";

const CartDrawer = () => {
  const { cartItems } = useSelector((state) => state.cart);
  const { isCartOpen } = useSelector((state) => state.ui);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const total = cartItems.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  const handleCheckout = () => {
    dispatch(closeCart());
    navigate("/shipping");
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => dispatch(closeCart())}
            aria-hidden="true"
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 w-80 md:w-96 h-full bg-[#f5f3ea] shadow-xl z-50 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            aria-label="Shopping bag"
          >
            {/* Header */}
            <div className="flex justify-between items-center px-5 py-4 border-b border-[#e2e1d8]">
              <h2 className="font-playfair text-[#1c1917] text-lg">
                Your Bag
                {cartItems.length > 0 && (
                  <span className="ml-2 text-sm font-sourcesans text-stone-400 font-normal">
                    ({cartItems.length})
                  </span>
                )}
              </h2>
              <button
                onClick={() => dispatch(closeCart())}
                aria-label="Close bag"
                className="p-1.5 rounded-md text-stone-500 hover:text-[#BC3F4A] hover:bg-stone-100 transition-colors"
              >
                <X className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {cartItems.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-stone-500 font-sourcesans text-sm mb-4">
                    Your bag is empty.
                  </p>
                  <button
                    onClick={() => dispatch(closeCart())}
                    className="text-sm text-[#BC3F4A] underline font-sourcesans"
                  >
                    Continue shopping
                  </button>
                </div>
              ) : (
                cartItems.map((item) => (
                  <div
                    key={item.product}
                    className="flex items-center gap-3 bg-white rounded-lg p-3 border border-stone-100"
                  >
                    <Link
                      to={`/products/${item.product}`}
                      onClick={() => dispatch(closeCart())}
                      className="shrink-0"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-14 h-14 object-cover rounded-md"
                      />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/products/${item.product}`}
                        onClick={() => dispatch(closeCart())}
                        className="font-sourcesans text-sm text-[#1c1917] hover:text-[#BC3F4A] transition-colors line-clamp-2"
                      >
                        {item.name}
                      </Link>
                      <p className="text-xs text-stone-500 mt-0.5">
                        {item.quantity} × ${item.price.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-[#1c1917] shrink-0">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="px-5 py-4 border-t border-[#e2e1d8] space-y-3 bg-[#f5f3ea]">
                {/* Total */}
                <div className="flex justify-between items-center text-sm">
                  <span className="font-sourcesans text-stone-600">Subtotal</span>
                  <span className="font-playfair text-[#1c1917] text-base">
                    ${total.toFixed(2)}
                  </span>
                </div>

                <p className="text-xs text-stone-400 text-center font-sourcesans">
                  70% goes to people serving in Ukraine
                </p>

                {/* Checkout — goes directly to shipping */}
                <button
                  onClick={handleCheckout}
                  className="w-full py-3 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-lg transition focus:outline-none focus:ring-2 focus:ring-[#BC3F4A] focus:ring-offset-2 text-sm"
                >
                  Checkout
                </button>

                {/* View full cart */}
                <Link
                  to="/cart"
                  onClick={() => dispatch(closeCart())}
                  className="block text-center text-xs text-stone-500 hover:text-[#BC3F4A] transition-colors font-sourcesans"
                >
                  View full bag
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;